/**
 * Fallback message resolution for $filters.responseMessage (bsapi#107).
 *
 * responseMessage matches only `alert_message.response_message` -- the shape
 * Helper::error_handler and Helper::success_handler emit. Every other envelope
 * fell through and showed NOTHING: not a generic error, silence. That covers
 * bsapi's bootstrap/app.php render handlers ({message, error_code} for 401,
 * validation 422, every HttpException and the production 500), StorageController's
 * bespoke {success:false, error:...} refusals, and hand-built call-site payloads
 * such as { data: { message: '...' } }.
 *
 * Returns null when nothing readable is present, so an unrecognised value stays
 * silent rather than toasting "undefined".
 */
export function fallbackMessage(val: any): { message: string; isError: boolean } | null {
    if (!val || typeof val !== 'object') return null;

    // axios.ts rejects with an Error carrying .data/.status; some call sites pass
    // the raw axios error instead, whose body sits under .response.data.
    const bodies: any[] = [val, val.data, val.response, val.response?.data].filter(
        (body) => body && typeof body === 'object'
    );

    let message = '';
    for (const body of bodies) {
        const candidate =
            (typeof body.alert_message === 'string' ? body.alert_message : body.alert_message?.response_message) ||
            body.message ||
            (typeof body.error === 'string' ? body.error : '') ||
            // Last, so a bag that also carries a response_message / message keeps
            // using that wording rather than a raw field error.
            firstBagMessage(body.alert_message) ||
            firstBagMessage(body.errors);
        if (typeof candidate === 'string' && candidate.trim() !== '') {
            message = candidate.trim();
            break;
        }
    }
    if (!message) return null;

    let status: number | null = null;
    for (const body of bodies) {
        const raw = body.status;
        if (typeof raw === 'number' && !isNaN(raw)) {
            status = raw;
            break;
        }
        if (typeof raw === 'string' && raw.trim() !== '' && !isNaN(Number(raw))) {
            status = Number(raw);
            break;
        }
    }

    // A failed request always reaches here with a status -- axios.ts stamps the
    // HTTP status onto the Error it rejects with. A payload carrying no status
    // and no error marker is therefore a hand-built notice, not a failure, and
    // reddening it would be a worse regression than the silence being fixed.
    // A field-error bag is itself an error marker: nothing emits one to say a
    // save went well.
    const hasErrorSignal =
        val instanceof Error ||
        bodies.some(
            (body) =>
                body.error_code ||
                body.errors ||
                body.success === false ||
                typeof body.error === 'string' ||
                (!body.alert_message?.response_message && !!firstBagMessage(body.alert_message))
        );

    return { message, isError: status !== null ? status >= 400 : hasErrorSignal };
}

/**
 * First human-readable line out of a Laravel MessageBag.
 *
 * `Helper::error_handler($validator->messages())` -- 101 call sites across 44
 * bsapi controllers -- puts the bag itself under `alert_message`, so the shape is
 * `{field: ["The Job Type field is required."], ...}` with NO `response_message`
 * anywhere in it. Both the lodash scan in responseMessage and the candidate walk
 * above read straight past that, which is why a failed save showed the user
 * nothing at all (BS-1210: rage clicks on Projects > Update).
 *
 * Only `alert_message` and `errors` are scanned this way. Walking every key of an
 * arbitrary body would turn unrelated string fields into toast text.
 */
function firstBagMessage(bag: any): string {
    if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return '';
    for (const value of Object.values(bag)) {
        if (typeof value === 'string' && value.trim() !== '') return value.trim();
        if (Array.isArray(value)) {
            const first = value.find((entry) => typeof entry === 'string' && entry.trim() !== '');
            if (first) return (first as string).trim();
        }
    }
    return '';
}
